import React, { useState, useEffect, useRef, useMemo, useContext } from 'react';
import { useMutation } from 'react-query';
import ProgressRing from 'components/common/ProgressRing';
import useGlobalStore from 'store/global';
import Button from 'components/common/Button';
import TextEditor, { TextEditorRef } from 'components/common/TextEditor';
import styles from './ReviewCommentBox.module.scss';
import { addReview } from 'service/course';
import { CourseDetailsContext } from 'components/CourseDetails';
import { Rating } from '@material-ui/lab';

export type CommentBoxProps = {
  callback?: () => void;
};
export default function ReviewCommentBox({
  callback,
}: CommentBoxProps): JSX.Element {
  const charLimit = 300;
  const setSnackbarMessage = useGlobalStore(
    (state) => state.setSnackbarMessage
  );
  const [newRating, setNewRating] = useState(0);
  const { courseInfo, refetchCourse } = useContext(CourseDetailsContext);
  const [postable, setPostable] = useState(false);
  // Text editor

  const [plainText, setPlainText] = useState('');
  const textLength = useMemo(() => plainText.length, [plainText]);
  const textEditorRef = useRef<TextEditorRef>();

  useEffect(() => {
    setPostable(textLength <= charLimit && newRating > 0);
  }, [textLength, charLimit, newRating]);

  // Make comment
  const { mutate: handleAddReview, isLoading: isMakingReview } = useMutation(
    async ({ comment, rating }: { comment: string; rating: number }) => {
      return await addReview({
        courseId: courseInfo.id,
        comment: comment,
        rating: rating,
      });
    },
    {
      onSuccess: () => {
        callback && callback();
        refetchCourse();
      },
      onError: () => {
        setSnackbarMessage({
          type: 'error',
          text: 'エラーが発生しました',
        });
      },
    }
  );

  return (
    <div className={styles.addReviewSection}>
      <div className={styles.title}>あなたのレビュー</div>
      <div className={styles.addRatingSection}>
        <Rating
          value={newRating}
          onChange={(e, value) => setNewRating(value)}
          size="large"
          name="add-rating"
        />
      </div>
      <div className={styles.commentBox}>
        <div className={styles.inputBox}>
          <div className={styles.textInputWrapper}>
            <div className={styles.textInput}>
              <TextEditor
                ref={textEditorRef}
                setPlainText={setPlainText}
                placeholder="レビューを入力する"
                readOnly={false}
                initialText=""
                charLimit={charLimit}
              />
            </div>
          </div>
          <div className={styles.footer}>
            <div className={styles.rightSide}>
              <div className={styles.progress}>
                <ProgressRing
                  radius={22}
                  current={textLength}
                  total={charLimit}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className={styles.addReviewButton}>
        <Button
          disabled={!postable}
          onClick={() => {
            handleAddReview({
              comment: textEditorRef.current?.getContent() || '',
              rating: newRating,
            });
          }}
          text="レビューを保存する"
          isLoading={isMakingReview}
        />
      </div>
    </div>
  );
}
